import { ChatBubbleOutline, Gavel, Info, Repeat } from "@mui/icons-material";
import UserThemedPaper from "./UserThemedPaper";
import UserTitle from "./UserTitle";
import {
  Box,
  IconButton,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { parsePostData } from "../utils/parsePostData";
import { baseUrl } from "../../../shared/config";

import ImagePostContent from "./ImagePostContent";
import LikePost from "./LikePost";
import SocialMediaPostExpanded from "./SocialMediaPostExpanded";
import { useState } from "react";
import PostActions from "./PostActions";
import VideoPostContent from "./PostContent/VideoPostContent";
import TextPostContent from "./PostContent/TextPostContent";
import moment from "moment";
import ExpandableText from "./ExpandableText";
import PostLikes from "./PostLikes";
import Repost from "./Repost";
import LoadingPostContent from "./PostContent/LoadingPost";
import { useProgressiveImage } from "../../../shared/hooks/useProgressiveImage";
import loadingGif from "../../../shared/assets/images/loading.gif";
import { makeStyles } from "@mui/styles";
const useStyles = makeStyles((theme) => ({
  "CompactPost-PostContent": ({ aspectRatio }) => ({
    aspectRatio,
    width: "100%",
    height: "max-content",
  }),
}));

const PostHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  "&>:first-child": {
    display: "flex",
    alignItems: "center",
  },
}));

const ThumbnailImg = styled("img")(({ theme }) => ({
  cursor: "pointer",
  objectFit: "cover",
  aspectRatio: "1 / 1",
}));

const PostThumbnail = ({ src, ...rest }) => {
  const { isSuccess, src: _src } = useProgressiveImage(src);
  return <ThumbnailImg src={isSuccess ? _src : loadingGif} {...rest} />;
};

const RenderContent = ({ postData, displayThumbnail, onExpand }) => {
  const classNames = useStyles({ aspectRatio: postData.proporcaoImagem });
  if (postData.ALUNOID_Gestor_Restritor != null) {
    return (
      <Box
        sx={{
          backgroundColor: "grey",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          color: "white",
        }}
        className="post_compact_thumbnail post_compact_thumbnail_restrict"
      >
        <Gavel />
        <br />
        <Typography>Esse post foi restringido</Typography>
      </Box>
    );
  }
  if (displayThumbnail && postData.TipoPostagem !== "TEXTO") {
    var contentThumbnail = postData.Link_Capa?.includes("http")
      ? postData.Link_Capa
      : `${baseUrl}/Membros/${postData.Link_Capa}`;
    const YTVideoIdRegEx = new RegExp(
      ".*(?:(?:youtu.be/|v/|vi/|u/w/|embed/|shorts/)|(?:(?:watch)??v(?:i)?=|&v(?:i)?=))([^#&?]*).*"
    );
    if (postData.TipoPostagem === "VIDEO") {
      try {
        if (postData.Link_URL) {
          const fileExtension = postData.Link_URL.split(".").pop();

          contentThumbnail = postData.Link_URL.replace(fileExtension, "jpg");
        }
        const videoId = postData.Link_youtube.match(YTVideoIdRegEx);
        if (postData.Link_youtube) {
          contentThumbnail = `https://img.youtube.com/vi/${videoId[1]}/0.jpg`;
        }
      } catch (e) {
        console.error(YTVideoIdRegEx);
      }
    }
    return (
      <PostThumbnail
        className="post_compact_thumbnail"
        src={contentThumbnail}
        onClick={onExpand}
      />
    );
  }

  switch (postData.TipoPostagem) {
    case "VIDEO":
      return (
        <VideoPostContent
          postData={postData}
          className={classNames["CompactPost-PostContent"]}
        />
      );

    case "IMAGEM":
      return (
        <ImagePostContent
          postData={postData}
          className={classNames["CompactPost-PostContent"]}
        />
      );

    case "GALLERY":
      return <>imagem</>;

    case "TEXTO":
      return (
        <TextPostContent
          postData={postData}
          onClick={onExpand}
          className={classNames["CompactPost-PostContent"]}
        />
      );

    case "INDEFINIDO":
      return <LoadingPostContent postData={postData} />;

    default:
      return <></>;
  }
};

export default function SocialMediaPostCompact({ postData, displayThumbnail }) {
  const parsedPostData = parsePostData(postData);
  const [expandedPostOpen, setExpandedPostOpen] = useState(false);

  const handleOpenExpandedPost = () => {
    setExpandedPostOpen(true);
  };

  const handleCloseExpandedPost = () => {
    setExpandedPostOpen(false);
  };
  const theme = useTheme();

  const postDateString = moment(
    postData.datapostagem,
    "DD/MM/YYYY HH:mm:ss"
  ).fromNow();

  return (
    <>
      <UserThemedPaper
        className="post_compact_wrapper"
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingBottom: theme.spacing(1),
          marginBottom: theme.spacing(2),
        }}
      >
        <PostHeader className="post_compact_header">
          <span>
            <UserTitle
              sx={{
                marginBottom: theme.spacing(1),
                paddingLeft: { xs: 1, md: 0 },
              }}
              userData={parsedPostData.authorData}
              secondary={
                postData.repostado_por &&
                `Repostado por ${postData.repostado_por}`
              }
            />
          </span>
          <span>
            {postData.ALUNOID_Gestor_Restritor != null && (
              <Tooltip
                title={
                  <>
                    Restrito por:{" "}
                    <UserTitle
                      userData={{
                        studentId: postData.ALUNOID_Gestor_Restritor,
                        name: postData.nome_gestor_restritor,
                        pictureURL: postData.fotoGestorRestritor,
                      }}
                    />
                  </>
                }
              >
                <Info color="error" />
              </Tooltip>
            )}
            <PostActions postData={postData} />
          </span>
        </PostHeader>

        <RenderContent
          onExpand={handleOpenExpandedPost}
          postData={postData}
          displayThumbnail={displayThumbnail}
        />
      </UserThemedPaper>
      <Box
        className="post_compact_footer"
        sx={{
          [theme.breakpoints.down("md")]: {
            paddingX: theme.spacing(1),
          },
        }}
      >
        <Typography sx={{ fontSize: "11px", fontWeight: "400" }}>
          {postDateString}
        </Typography>
        <Box
          sx={{
            width: "fit-content",
            "&>:first-child": {
              paddingLeft: "0px !important",
            },
          }}
        >
          <LikePost
            postId={postData.postagemid}
            themeId={postData.temaid}
            liked={postData.liked_from_me}
          />
          <IconButton onClick={handleOpenExpandedPost}>
            <ChatBubbleOutline />
          </IconButton>

          <Repost postData={postData}>
            {({ openDialog }) => {
              return (
                <IconButton onClick={openDialog}>
                  <Repeat />
                  <span>{postData.qtd_repost}</span>
                </IconButton>
              );
            }}
          </Repost>
        </Box>
        <PostLikes postId={postData.postagemid}>
          {({ openDialog }) => {
            return (
              <Typography
                sx={{ cursor: "pointer" }}
                onClick={openDialog}
                variant="subtitle2"
              >
                <b>
                  {postData.qtd_likes}
                  &nbsp; curtidas
                </b>
              </Typography>
            );
          }}
        </PostLikes>

        <ExpandableText value={postData.texto || ""} notExpandedSize={300} />

        <Typography
          className="cursor-pointer"
          variant="subtitle2"
          onClick={handleOpenExpandedPost}
          fontWeight={"600"}
        >
          {postData.qtd_comentario
            ? postData.qtd_comentario === 1
              ? `Ver todos  comentários...`
              : `Ver todos ${postData.qtd_comentario} comentário(s)...`
            : `Adicione um comentário`}
        </Typography>
      </Box>
      <SocialMediaPostExpanded
        open={expandedPostOpen}
        postData={postData}
        onClose={handleCloseExpandedPost}
      />
    </>
  );
}
